import { Copy, Sparkles } from "lucide-react";
import { useState } from "react";
import { ClassificationBadge } from "@/components/ci/classification-badge";
import { FailurePhaseBadge } from "@/components/ci/failure-phase-badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CiTestResult } from "@/lib/types";

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch {
          // Clipboard unavailable on non-HTTPS origins — leave state idle.
        }
      }}
      aria-label="Copy to clipboard"
      className="text-muted-foreground hover:text-foreground transition-colors"
      title="Copy to clipboard"
    >
      <Copy className="size-3.5" />
      {copied && <span className="ml-1 text-xs text-emerald-500">Copied</span>}
    </button>
  );
}

export function CiFailureCard({ result }: { result: CiTestResult }) {
  const classification = result.error_classification;
  const detail = result.error_classification_detail;
  const headline = detail || result.error || "Test failed";
  const hasAnalysis = Boolean(classification || detail || result.ai_summary);

  return (
    <Card className="border-rose-500/30">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="text-sm text-rose-500">Failure</CardTitle>
            {classification && <ClassificationBadge classification={classification} />}
            {result.failure_phase && <FailurePhaseBadge phase={result.failure_phase} />}
          </div>
          {result.error && <CopyButton text={result.error} />}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="font-mono text-sm break-words">{headline}</p>

        {result.error && result.error !== headline && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Raw error
            </summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono">
              {result.error}
            </pre>
          </details>
        )}

        <AiSummarySection result={result} hasOtherAnalysis={hasAnalysis} />
      </CardContent>
    </Card>
  );
}

function AiMetaRow({ result, label }: { result: CiTestResult; label: string }) {
  return (
    <div className="mb-2 flex items-center gap-1.5">
      <Sparkles className="size-3.5 text-purple-400" />
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="ml-auto text-[10px] text-muted-foreground">
        {result.ai_summary_model || "claude"}
        {result.ai_summary_generated_at && (
          <>
            {" · "}
            {new Date(result.ai_summary_generated_at).toLocaleString(undefined, {
              timeZone: "UTC",
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            (UTC)
          </>
        )}
      </span>
    </div>
  );
}

function AiSummarySection({
  result,
  hasOtherAnalysis,
}: {
  result: CiTestResult;
  hasOtherAnalysis: boolean;
}) {
  const analysis = result.ai_analysis;
  if (analysis) {
    const showFailedStage =
      analysis.failed_stage !== "unknown" && analysis.failed_stage !== result.failure_phase;
    return (
      <div className="rounded-md border border-border/60 bg-muted/30 p-3">
        <AiMetaRow result={result} label="AI analysis" />
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {showFailedStage && <FailurePhaseBadge phase={analysis.failed_stage} />}
            <p className="text-sm font-medium leading-snug">{analysis.root_cause}</p>
          </div>
          {analysis.evidence.map((evidenceItem) => (
            <div key={`${evidenceItem.source_file}-${evidenceItem.quote}`}>
              <div className="font-mono text-[10px] text-muted-foreground">
                {evidenceItem.source_file}
              </div>
              <pre className="mt-0.5 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 font-mono text-xs">
                {evidenceItem.quote}
              </pre>
            </div>
          ))}
          {analysis.suggested_next_step && (
            <p className="text-sm">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Next step:{" "}
              </span>
              {analysis.suggested_next_step}
            </p>
          )}
          {analysis.confidence !== "high" && (
            <p className="text-[10px] text-muted-foreground">confidence: {analysis.confidence}</p>
          )}
        </div>
      </div>
    );
  }

  if (result.ai_summary) {
    // Legacy freeform summary (pre-structured-analysis rows). Collapsed by
    // default — the stage flow + heuristic headline carry the signal now.
    return (
      <details className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs">
        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-purple-400" />
            AI summary
          </span>
        </summary>
        <div className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground/90">
          {result.ai_summary}
        </div>
      </details>
    );
  }

  // No AI analysis present. Be quiet about it — no need for a placeholder
  // when the heuristic already explained the failure.
  if (hasOtherAnalysis) {
    return null;
  }
  return (
    <p className="text-xs text-muted-foreground">AI analysis not generated for this failure.</p>
  );
}
